import { Card } from '@/components/ui/card';
import { BarChart, Smartphone, Clock, Rss } from 'lucide-react';

export interface StatusCardsProps {
  activeSensors: number;
  connectedDevices: number;
  dataPoints: number;
  isLiveData: boolean;
  isLoading?: boolean;
  className?: string;
}

interface StatusCardData {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  bgColor: string;
  iconColor: string;
}

/**
 * Status cards component for analytics dashboard
 * Displays key metrics in a responsive card layout
 */
export function StatusCards({
  activeSensors,
  connectedDevices,
  dataPoints,
  isLiveData,
  isLoading = false,
  className = '',
}: StatusCardsProps) {
  const statusCards: StatusCardData[] = [
    {
      icon: BarChart,
      label: 'Active Sensors',
      value: isLoading ? '...' : activeSensors,
      bgColor: 'bg-blue-100 dark:bg-blue-900/20',
      iconColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      icon: Smartphone,
      label: 'Connected Devices',
      value: isLoading ? '...' : connectedDevices,
      bgColor: 'bg-green-100 dark:bg-green-900/20',
      iconColor: 'text-green-600 dark:text-green-400',
    },
    {
      icon: Clock,
      label: 'Data Points',
      value: isLoading ? '...' : typeof dataPoints === 'number' ? dataPoints.toLocaleString() : dataPoints,
      bgColor: 'bg-purple-100 dark:bg-purple-900/20',
      iconColor: 'text-purple-600 dark:text-purple-400',
    },
    {
      icon: Rss,
      label: 'Data Status',
      value: isLiveData ? 'Live' : 'Historical',
      bgColor: isLiveData ? 'bg-green-100 dark:bg-green-900/20' : 'bg-gray-100 dark:bg-gray-900/20',
      iconColor: isLiveData ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400',
    },
  ];

  return (
    <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 ${className}`}>
      {statusCards.map((card, index) => {
        const IconComponent = card.icon;

        return (
          <Card key={index} className="p-4">
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-full ${card.bgColor}`}>
                <IconComponent className={`h-5 w-5 ${card.iconColor}`} />
              </div>
              <div>
                <p className="text-sm font-medium">{card.label}</p>
                <p className="text-2xl font-bold">{card.value}</p>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
